import React, { useState } from 'react';
import { X, RotateCw, Monitor, Pencil, ChevronDown, ChevronUp, CheckCircle2, XCircle, SkipForward, Clock, Loader2 } from 'lucide-react';
import type { EcadAutomacaoPasso } from './types';

export interface DryRunReportItem {
  ordem: number;
  acao: string;
  selectorOriginal: string;
  selectorResolvido: string | null;
  status: 'PASS' | 'FAIL' | 'SKIP' | 'NO_SELECTOR';
  motivo?: string;
  screenshot?: string; // base64
}

interface EcadDryRunPanelProps {
  report: DryRunReportItem[];
  durationMs: number;
  aprovados: number;
  falhas: number;
  pulados: number;
  onClose: () => void;
  onRetryStep: (ordem: number) => Promise<void>;
  onOpenPaused: (ordem: number) => Promise<void>;
  onEditStep: (ordem: number) => void;
  retryingStep: number | null;
  openingStep: number | null;
  passos: EcadAutomacaoPasso[];
}

export const EcadDryRunPanel: React.FC<EcadDryRunPanelProps> = ({
  report,
  durationMs,
  aprovados,
  falhas,
  pulados,
  onClose,
  onRetryStep,
  onOpenPaused,
  onEditStep,
  retryingStep,
  openingStep,
  passos,
}) => {
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [zoomScreenshot, setZoomScreenshot] = useState<string | null>(null);

  const selectedItem = report.find(r => r.ordem === selectedStep);
  const selectedPasso = passos.find(p => p.ordem === selectedStep);

  const statusIcon = (status: string) => {
    switch (status) {
      case 'PASS': return <CheckCircle2 size={14} style={{ color: '#10b981' }} />;
      case 'FAIL': return <XCircle size={14} style={{ color: '#ef4444' }} />;
      default: return <SkipForward size={14} style={{ color: '#64748b' }} />;
    }
  };

  const statusBg = (status: string) => {
    switch (status) {
      case 'PASS': return '#ecfdf5';
      case 'FAIL': return '#fef2f2';
      default: return '#f8fafc';
    }
  };

  const statusBorder = (status: string) => {
    switch (status) {
      case 'PASS': return '1px solid #a7f3d0';
      case 'FAIL': return '1px solid #fecaca';
      default: return '1px solid #e2e8f0';
    }
  };

  return (
    <>
      {/* Zoom overlay */}
      {zoomScreenshot && (
        <div
          onClick={() => setZoomScreenshot(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <img
            src={zoomScreenshot.startsWith('data:') ? zoomScreenshot : `data:image/jpeg;base64,${zoomScreenshot}`}
            alt="Screenshot zoom"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.1)' }}
          />
        </div>
      )}

      <div style={{
        background: '#ffffff',
        border: '1px solid #cbd5e1',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 300px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1rem' }}>🧪</span>
            <div>
              <h3 style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                Resultado Dry-Run
              </h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                <span style={{ fontSize: '0.68rem', color: '#047857', fontWeight: 700 }}>{aprovados}✅</span>
                <span style={{ fontSize: '0.68rem', color: '#b91c1c', fontWeight: 700 }}>{falhas}❌</span>
                <span style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 700 }}>{pulados}⏭️</span>
                <span style={{ fontSize: '0.68rem', color: '#64748b' }}>
                  <Clock size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                  {(durationMs / 1000).toFixed(1)}s
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '4px',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ height: '3px', background: '#cbd5e1', display: 'flex' }}>
          {report.length > 0 && (
            <>
              <div style={{ width: `${(aprovados / report.length) * 100}%`, background: '#10b981', transition: 'width 0.5s' }} />
              <div style={{ width: `${(falhas / report.length) * 100}%`, background: '#ef4444', transition: 'width 0.5s' }} />
              <div style={{ width: `${(pulados / report.length) * 100}%`, background: '#475569', transition: 'width 0.5s' }} />
            </>
          )}
        </div>

        {/* Content: Timeline + Inspector */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Timeline */}
          <div style={{
            flex: selectedStep ? '0 0 55%' : 1,
            overflowY: 'auto',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            transition: 'flex 0.3s',
          }}>
            {report.map((item) => {
              const isSelected = selectedStep === item.ordem;
              return (
                <div
                  key={item.ordem}
                  onClick={() => setSelectedStep(isSelected ? null : item.ordem)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    background: isSelected ? '#f5f3ff' : statusBg(item.status),
                    border: isSelected ? '1px solid #ddd6fe' : statusBorder(item.status),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minHeight: '44px',
                  }}
                >
                  {/* Status icon */}
                  <div style={{ flexShrink: 0, width: '20px', display: 'flex', justifyContent: 'center' }}>
                    {statusIcon(item.status)}
                  </div>

                  {/* Step number */}
                  <span style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: item.status === 'PASS' ? '#d1fae5' : item.status === 'FAIL' ? '#fee2e2' : '#f1f5f9',
                    border: item.status === 'PASS' ? '1px solid #10b981' : item.status === 'FAIL' ? '1px solid #ef4444' : '1px solid #cbd5e1',
                    color: item.status === 'PASS' ? '#047857' : item.status === 'FAIL' ? '#991b1b' : '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    flexShrink: 0,
                  }}>
                    {item.ordem}
                  </span>

                  {/* Action type */}
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    minWidth: '60px',
                  }}>
                    {item.acao}
                  </span>

                  {/* Screenshot thumbnail */}
                  {item.screenshot && (
                    <img
                      src={`data:image/jpeg;base64,${item.screenshot}`}
                      alt={`Step ${item.ordem}`}
                      style={{
                        width: '48px',
                        height: '27px',
                        objectFit: 'cover',
                        borderRadius: '3px',
                        border: '1px solid #cbd5e1',
                        flexShrink: 0,
                        cursor: 'zoom-in',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomScreenshot(item.screenshot!);
                      }}
                    />
                  )}

                  {/* Motivo (truncated) */}
                  <span style={{
                    flex: 1,
                    fontSize: '0.65rem',
                    color: item.status === 'FAIL' ? '#b91c1c' : '#475569',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}>
                    {item.status === 'FAIL' ? item.selectorOriginal : (item.motivo || '')}
                  </span>

                  {/* Expand indicator */}
                  <div style={{ flexShrink: 0, color: '#475569' }}>
                    {isSelected ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Inspector Panel */}
          {selectedStep && selectedItem && (
            <div style={{
              flex: '0 0 45%',
              borderLeft: '1px solid rgba(255, 255, 255, 0.06)',
              overflowY: 'auto',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              background: 'rgba(2, 6, 23, 0.3)',
            }}>
              {/* Inspector Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {statusIcon(selectedItem.status)}
                <h4 style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', margin: 0, flex: 1 }}>
                  Passo {selectedItem.ordem} — {selectedItem.acao}
                </h4>
                <button
                  onClick={() => setSelectedStep(null)}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '2px' }}
                >
                  <X size={12} />
                </button>
              </div>

              {/* Screenshot */}
              {selectedItem.screenshot && (
                <div
                  style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1', cursor: 'zoom-in' }}
                  onClick={() => setZoomScreenshot(selectedItem.screenshot!)}
                >
                  <img
                    src={`data:image/jpeg;base64,${selectedItem.screenshot}`}
                    alt={`Step ${selectedItem.ordem}`}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
              )}

              {/* Details */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Seletor */}
                <div style={{
                  padding: '8px 10px',
                  background: '#ffffff',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                }}>
                  <div style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Seletor Original
                  </div>
                  <code style={{ fontSize: '0.72rem', color: '#c084fc', wordBreak: 'break-all' }}>
                    {selectedItem.selectorOriginal || '(nenhum)'}
                  </code>
                </div>

                {selectedItem.selectorResolvido && selectedItem.selectorResolvido !== selectedItem.selectorOriginal && (
                  <div style={{
                    padding: '8px 10px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                  }}>
                    <div style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Seletor Resolvido
                    </div>
                    <code style={{ fontSize: '0.72rem', color: '#2563eb', wordBreak: 'break-all' }}>
                      {selectedItem.selectorResolvido}
                    </code>
                  </div>
                )}

                {/* Valor */}
                {selectedPasso?.valor && (
                  <div style={{
                    padding: '8px 10px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                  }}>
                    <div style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Valor
                    </div>
                    <code style={{ fontSize: '0.72rem', color: '#059669' }}>
                      {selectedPasso.valor}
                    </code>
                  </div>
                )}

                {/* Legenda */}
                {selectedPasso?.legenda && (
                  <div style={{
                    padding: '8px 10px',
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                  }}>
                    <div style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Legenda / Fala
                    </div>
                    <p style={{ fontSize: '0.72rem', color: '#334155', margin: 0, lineHeight: 1.45 }}>
                      {selectedPasso.legenda}
                    </p>
                  </div>
                )}

                {/* Error message */}
                {selectedItem.status === 'FAIL' && selectedItem.motivo && (
                  <div style={{
                    padding: '8px 10px',
                    background: '#fef2f2',
                    borderRadius: '6px',
                    border: '1px solid #fecaca',
                  }}>
                    <div style={{ fontSize: '0.62rem', color: '#b91c1c', fontWeight: 700, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Erro
                    </div>
                    <p style={{ fontSize: '0.72rem', color: '#991b1b', margin: 0, lineHeight: 1.4, wordBreak: 'break-word' }}>
                      {selectedItem.motivo}
                    </p>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                {selectedItem.status === 'FAIL' && (
                  <>
                    <button
                      onClick={() => onRetryStep(selectedItem.ordem)}
                      disabled={retryingStep === selectedItem.ordem}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        cursor: retryingStep === selectedItem.ordem ? 'wait' : 'pointer',
                        background: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        color: '#047857',
                        opacity: retryingStep === selectedItem.ordem ? 0.6 : 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      {retryingStep === selectedItem.ordem ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
                      Repetir
                    </button>
                    <button
                      onClick={() => onOpenPaused(selectedItem.ordem)}
                      disabled={openingStep === selectedItem.ordem}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        cursor: openingStep === selectedItem.ordem ? 'wait' : 'pointer',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        opacity: openingStep === selectedItem.ordem ? 0.6 : 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      {openingStep === selectedItem.ordem ? <Loader2 size={12} className="animate-spin" /> : <Monitor size={12} />}
                      Abrir Tela
                    </button>
                  </>
                )}
                <button
                  onClick={() => onEditStep(selectedItem.ordem)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: '#f5f3ff',
                    border: '1px solid #ddd6fe',
                    color: '#7c3aed',
                    transition: 'all 0.2s',
                  }}
                >
                  <Pencil size={12} />
                  Editar Passo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
